package dev.danko.roblox_craft_ai.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.client.RobloxCraftApiClient
import dev.danko.roblox_craft_ai.components.LegalDoc
import dev.danko.roblox_craft_ai.components.SiteFooter
import dev.danko.roblox_craft_ai.components.legalPath
import dev.danko.roblox_craft_ai.shared.model.AccessRequestDto
import dev.danko.roblox_craft_ai.state.AuthState
import dev.danko.roblox_craft_ai.uikit.components.AppButton
import dev.danko.roblox_craft_ai.uikit.components.AppButtonSize
import dev.danko.roblox_craft_ai.uikit.components.AppButtonVariant
import dev.danko.roblox_craft_ai.uikit.components.AppCard
import dev.danko.roblox_craft_ai.uikit.components.AppInput
import dev.danko.roblox_craft_ai.uikit.components.LanguageSwitcher
import dev.danko.roblox_craft_ai.uikit.i18n.LocaleController
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import dev.danko.roblox_craft_ai.uikit.icons.AppIcon
import dev.danko.roblox_craft_ai.uikit.icons.Icon
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.CheckboxInput
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea

/**
 * Promo page of the invite-only phase: what the service does and a request form. Signed-in users go straight to the
 * workshop; `?signin=1` (the link in the approval email) opens the sign-in dialog.
 */
@Composable
@Page("/welcome")
fun WelcomePage() {
    val s = strings().access
    val palette = LocalAppTheme.current.colors

    LaunchedEffect(AuthState.ready, AuthState.user) {
        if (AuthState.ready && AuthState.user != null) window.location.replace("/")
    }
    LaunchedEffect(Unit) {
        document.title = "Craftiva AI — ${s.badge}"
        if (window.location.search.contains("signin=1")) AuthState.loginDialogOpen = true
    }

    Column(Modifier.fillMaxWidth().minHeight(100.vh).backgroundColor(palette.background)) {
        // Wraps on phones: logo on the first line, language and sign-in on the second.
        Row(
            Modifier.fillMaxWidth().padding(topBottom = 12.px, leftRight = 16.px).gap(10.px).flexWrap(FlexWrap.Wrap)
                .styleModifier { property("border-bottom", "1px solid ${palette.border}") },
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(Modifier.gap(10.px).flexGrow(1), verticalAlignment = Alignment.CenterVertically) {
                Box(
                    Modifier.size(36.px).borderRadius(10.px).backgroundColor(palette.accent).color(palette.textPrimary)
                        .styleModifier { property("flex-shrink", "0") },
                    contentAlignment = Alignment.Center
                ) { Icon(AppIcon.LOGO, size = 20) }
                SpanText("Craftiva AI", Modifier.fontSize(17.px).fontWeight(FontWeight.Bold).color(palette.textPrimary).whiteSpace(com.varabyte.kobweb.compose.css.WhiteSpace.NoWrap))
            }
            Row(Modifier.gap(10.px), verticalAlignment = Alignment.CenterVertically) {
                LanguageSwitcher()
                AppButton(text = s.signIn, icon = AppIcon.USER, onClick = { AuthState.loginDialogOpen = true }, variant = AppButtonVariant.SECONDARY, size = AppButtonSize.SM)
            }
        }

        Box(Modifier.fillMaxWidth().padding(leftRight = 20.px), contentAlignment = Alignment.TopCenter) {
            Row(
                Modifier.fillMaxWidth().maxWidth(72.cssRem).padding(topBottom = 3.cssRem).gap(2.5.cssRem).flexWrap(FlexWrap.Wrap),
                verticalAlignment = Alignment.Top
            ) {
                Column(Modifier.flexGrow(1).flexBasis(26.cssRem).gap(1.25.cssRem)) {
                    SpanText(
                        s.badge,
                        Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold).color(palette.accentText)
                            .padding(topBottom = 4.px, leftRight = 12.px).borderRadius(999.px)
                            .backgroundColor(palette.accent.toRgb().copy(alpha = 40)).width(com.varabyte.kobweb.compose.css.Width.FitContent)
                    )
                    SpanText(
                        s.title,
                        Modifier.fontSize(2.6.cssRem).fontWeight(FontWeight.ExtraBold).lineHeight(1.12).color(palette.textPrimary)
                            .styleModifier { property("letter-spacing", "-0.02em") }
                    )
                    SpanText(s.subtitle, Modifier.fontSize(1.05.cssRem).lineHeight(1.6).color(palette.textSecondary))
                    Column(Modifier.gap(0.75.cssRem).margin(top = 0.5.cssRem)) {
                        listOf(
                            Triple(AppIcon.CODE, s.f1Title, s.f1Text),
                            Triple(AppIcon.CUBE, s.f2Title, s.f2Text),
                            Triple(AppIcon.SEND, s.f3Title, s.f3Text)
                        ).forEach { (icon, title, text) -> Feature(icon, title, text) }
                    }
                }
                Box(Modifier.flexGrow(1).flexBasis(22.cssRem).maxWidth(30.cssRem)) {
                    if (AuthState.inviteOnly || !AuthState.ready) RequestForm() else OpenAccessCard()
                }
            }
        }
        Spacer()
        SiteFooter()
    }
}

@Composable
private fun Feature(icon: AppIcon, title: String, text: String) {
    val palette = LocalAppTheme.current.colors
    Row(Modifier.gap(14.px), verticalAlignment = Alignment.Top) {
        Box(
            Modifier.size(40.px).borderRadius(12.px).backgroundColor(palette.surfaceElevated).color(palette.accentText)
                .styleModifier { property("flex-shrink", "0") },
            contentAlignment = Alignment.Center
        ) { Icon(icon, size = 20) }
        Column(Modifier.gap(2.px)) {
            SpanText(title, Modifier.fontWeight(FontWeight.SemiBold).color(palette.textPrimary))
            SpanText(text, Modifier.fontSize(0.92.cssRem).lineHeight(1.5).color(palette.textSecondary))
        }
    }
}

/** Shown when the service is open: no request needed. */
@Composable
private fun OpenAccessCard() {
    AppCard(Modifier.fillMaxWidth()) {
        Column(Modifier.gap(1.cssRem)) {
            AppButton(text = strings().auth.toWorkshop, onClick = { window.location.href = "/" }, fullWidth = true)
        }
    }
}

@Composable
private fun RequestForm() {
    val s = strings().access
    val auth = strings().auth
    val palette = LocalAppTheme.current.colors
    val scope = rememberCoroutineScope()
    var email by remember { mutableStateOf("") }
    var name by remember { mutableStateOf("") }
    var message by remember { mutableStateOf("") }
    var ageConfirmed by remember { mutableStateOf(false) }
    var privacyAccepted by remember { mutableStateOf(false) }
    var sending by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var sentTo by remember { mutableStateOf<String?>(null) }

    AppCard(Modifier.fillMaxWidth()) {
        val sent = sentTo
        if (sent != null) {
            Column(Modifier.gap(0.75.cssRem).padding(topBottom = 1.cssRem), horizontalAlignment = Alignment.CenterHorizontally) {
                Box(
                    Modifier.size(56.px).borderRadius(999.px).backgroundColor(palette.statusReady.toRgb().copy(alpha = 40)).color(palette.statusReady),
                    contentAlignment = Alignment.Center
                ) { Icon(AppIcon.CHECK, size = 28) }
                SpanText(s.sentTitle, Modifier.fontSize(1.3.cssRem).fontWeight(FontWeight.Bold).color(palette.textPrimary))
                SpanText(s.sentText(sent), Modifier.textAlign(TextAlign.Center).color(palette.textSecondary).lineHeight(1.55))
            }
            return@AppCard
        }
        Column(Modifier.gap(0.9.cssRem)) {
            SpanText(s.formTitle, Modifier.fontSize(1.3.cssRem).fontWeight(FontWeight.Bold).color(palette.textPrimary))
            SpanText(s.formHint, Modifier.fontSize(0.9.cssRem).lineHeight(1.5).color(palette.textSecondary))
            AppInput(
                value = email, onValueChanged = { email = it; error = null },
                label = strings().auth.emailLabel, placeholder = strings().auth.emailPlaceholder,
                type = InputType.Email, disabled = sending
            )
            AppInput(value = name, onValueChanged = { name = it.take(100) }, label = s.nameLabel, placeholder = s.namePlaceholder, disabled = sending)
            Column(Modifier.fillMaxWidth().gap(6.px)) {
                SpanText(s.messageLabel, Modifier.fontSize(14.px).fontWeight(FontWeight.Medium).color(palette.textPrimary))
                TextArea(
                    value = message,
                    attrs = Modifier.fillMaxWidth().height(88.px).padding(10.px).borderRadius(10.px)
                        .backgroundColor(palette.surfaceElevated).color(palette.textPrimary)
                        .styleModifier {
                            property("width", "100%")
                            property("box-sizing", "border-box")
                            property("border", "1px solid ${palette.border}")
                            property("font", "inherit")
                            property("resize", "vertical")
                        }
                        .toAttrs {
                            attr("placeholder", s.messagePlaceholder)
                            attr("maxlength", "1000")
                            onInput { message = it.value }
                        }
                )
            }
            Consent(checked = ageConfirmed, onChange = { ageConfirmed = it; error = null }) { Text(s.ageConfirm) }
            Consent(checked = privacyAccepted, onChange = { privacyAccepted = it; error = null }) {
                A(legalPath(LegalDoc.PRIVACY), Modifier.color(palette.accentText).toAttrs { attr("target", "_blank") }) { Text(s.privacyAccept) }
            }
            error?.let { SpanText(it, Modifier.fontSize(0.85.cssRem).color(palette.statusFailed)) }
            AppButton(
                text = s.submit,
                icon = AppIcon.SEND,
                loading = sending,
                enabled = email.isNotBlank() && !sending,
                fullWidth = true,
                onClick = {
                    if (!ageConfirmed || !privacyAccepted) {
                        error = s.consentRequired
                        return@AppButton
                    }
                    sending = true
                    scope.launch {
                        val code = RobloxCraftApiClient.requestAccess(
                            AccessRequestDto(
                                email = email.trim(), name = name.trim(), message = message.trim(),
                                locale = LocaleController.current.code, ageConfirmed = true, privacyAccepted = true
                            )
                        )
                        sending = false
                        when (code) {
                            null -> sentTo = email.trim()
                            "INVALID_EMAIL" -> error = auth.invalidEmail
                            "RATE_LIMIT_EXCEEDED" -> error = auth.throttled
                            "CONSENT_REQUIRED" -> error = s.consentRequired
                            else -> error = s.sendFailed
                        }
                    }
                }
            )
            Row(Modifier.gap(6.px).fontSize(0.9.cssRem), verticalAlignment = Alignment.CenterVertically) {
                SpanText(s.alreadyApproved, Modifier.color(palette.textSecondary))
                AppButton(text = s.signIn, onClick = { AuthState.loginDialogOpen = true }, variant = AppButtonVariant.GHOST, size = AppButtonSize.SM)
            }
        }
    }
}

@Composable
private fun Consent(checked: Boolean, onChange: (Boolean) -> Unit, label: @Composable () -> Unit) {
    val palette = LocalAppTheme.current.colors
    Label(attrs = Modifier.display(org.jetbrains.compose.web.css.DisplayStyle.Flex).gap(10.px).fontSize(0.88.cssRem).lineHeight(1.45)
        .color(palette.textSecondary).cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
        .styleModifier { property("align-items", "flex-start") }.toAttrs()) {
        CheckboxInput(checked = checked, attrs = Modifier.margin(top = 3.px).styleModifier {
            property("accent-color", palette.accent.toString())
            property("width", "16px"); property("height", "16px"); property("flex-shrink", "0")
        }.toAttrs { onChange { onChange(it.value) } })
        org.jetbrains.compose.web.dom.Span { label() }
    }
}
